/**
 * Store
 */
import config from 'config';
import { createWrapper } from 'next-redux-wrapper';
import { applyMiddleware, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import { CacheService } from 'services/cache.service';

import {
  analyticsSearchMiddleware,
  cmsServiceMiddleware,
  conversionsMiddleware,
  likeServiceMiddleware,
  referralServiceMiddleware,
  userDac7ServiceMiddleware,
} from './middleWares';
import rootReducer from './root-reducer';

// Middleware for the store
const middleWare = [
  thunk,
  likeServiceMiddleware,
  cmsServiceMiddleware,
  referralServiceMiddleware,
  userDac7ServiceMiddleware,
  analyticsSearchMiddleware,
  conversionsMiddleware,
];

const env = config.get('env');

const isDevEnv = ['dev', 'local'].includes(env);

// Redux-logger middleware
if (isDevEnv && config.get('development.logger')) {
  const logger = createLogger({
    // ...options
  });

  middleWare.push(logger);
}

// Initial state for the store
const initialState = {};

// For redux store debugging in development mode
const appliedMiddleware = isDevEnv ? composeWithDevTools(applyMiddleware(...middleWare)) : applyMiddleware(...middleWare);

// Type the store properly for TypeScript
export type RootState = ReturnType<typeof rootReducer>;
type AppStore = Store<RootState>;

// Client-side singleton store instance (will be initialized on first call)
let clientStore: AppStore | undefined;

// CRITICAL: Hybrid factory/singleton pattern
// - Server-side (SSR): Creates new store for each request (prevents race conditions)
// - Client-side: Reuses same singleton instance (ensures checkLogin() and components share same store)
export const makeStore = (): AppStore => {
  // Client-side: Always return the same singleton instance
  if (typeof window !== 'undefined') {
    if (!clientStore) {
      clientStore = createStore(rootReducer, initialState, appliedMiddleware) as AppStore;
    }

    return clientStore;
  }

  // Server-side (SSR): Create new store for each request (factory pattern)
  return createStore(rootReducer, initialState, appliedMiddleware) as AppStore;
};

// Initialize client-side singleton
const store = makeStore();

export type AppDispatch = typeof store.dispatch;

// Export store methods for direct usage (client-side only)
export const { dispatch, subscribe, getState } = store;
export default store;

// CRITICAL: Pass factory function (not instance) to create new store per SSR request
export const wrapper = createWrapper<AppStore>(makeStore, {
  serializeState: (state) => {
    if (typeof state === 'string') {
      return state;
    }

    try {
      return JSON.stringify(state);
    } catch (error) {
      return state;
    }
  },
  deserializeState: (state) => {
    if (typeof state !== 'string') {
      return state;
    }

    try {
      return JSON.parse(state);
    } catch (error) {
      return state;
    }
  },
});

export const cache = new CacheService({ defaultMaxAgeSeconds: 5 * 60 });
